/**
 * Robust retry helper for network requests in AWS environments
 * Handles race conditions, timeouts, and transient network failures
 */

export interface RetryOptions {
    maxAttempts?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffMultiplier?: number;
    timeout?: number;
    retryCondition?: (error: any) => boolean;
}

export interface RetryResult<T> {
    success: boolean;
    data?: T;
    error?: string;
    attempts: number;
    totalTime: number;
}

/**
 * Default retry condition for network errors commonly seen in AWS
 */
const defaultRetryCondition = (error: any): boolean => {
    // Retry on network errors, timeouts, and 5xx server errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return true; // Network error
    }
    
    if (error.name === 'AbortError') {
        return true; // Timeout
    }
    
    if (error.status >= 500 && error.status < 600) {
        return true; // Server errors
    }
    
    if (error.status === 429) {
        return true; // Rate limiting
    }
    
    // Retry on specific AWS/networking errors
    const retryableErrors = [
        'NETWORK_ERROR',
        'TIMEOUT',
        'CONNECTION_RESET',
        'ECONNRESET',
        'ENOTFOUND',
        'ECONNREFUSED',
        'Failed to fetch'
    ];
    
    const errorMessage = error.message || '';
    return retryableErrors.some(retryableError => 
        errorMessage.includes(retryableError)
    );
};

/**
 * Sleep utility for delays
 */
const sleep = (ms: number): Promise<void> => 
    new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay with jitter
 */
const calculateDelay = (
    attempt: number, 
    initialDelay: number, 
    maxDelay: number, 
    backoffMultiplier: number
): number => {
    const exponentialDelay = initialDelay * Math.pow(backoffMultiplier, attempt - 1);
    const cappedDelay = Math.min(exponentialDelay, maxDelay);
    
    // Add jitter to prevent thundering herd in distributed environments
    const jitter = cappedDelay * 0.1 * Math.random();
    return cappedDelay + jitter;
};

/**
 * Robust retry wrapper for async operations
 */
export async function withRetry<T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
): Promise<RetryResult<T>> {
    const {
        maxAttempts = 3,
        initialDelay = 1000,
        maxDelay = 10000,
        backoffMultiplier = 2,
        timeout = 30000,
        retryCondition = defaultRetryCondition
    } = options;

    const startTime = Date.now();
    let lastError: any;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            // Create timeout promise
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => {
                    reject(new Error(`Request timeout after ${timeout}ms`));
                }, timeout);
            });

            // Race the operation against timeout
            const result = await Promise.race([
                operation(),
                timeoutPromise
            ]);

            // Success!
            return {
                success: true,
                data: result,
                attempts: attempt,
                totalTime: Date.now() - startTime
            };

        } catch (error: any) {
            lastError = error;
            
            console.warn(`Attempt ${attempt}/${maxAttempts} failed:`, {
                error: error.message,
                status: error.status,
                attempt,
                willRetry: attempt < maxAttempts && retryCondition(error)
            });

            // Don't retry if this is the last attempt or error is not retryable
            if (attempt >= maxAttempts || !retryCondition(error)) {
                break;
            }

            // Calculate delay with exponential backoff and jitter
            const delay = calculateDelay(attempt, initialDelay, maxDelay, backoffMultiplier);
            
            console.log(`Retrying in ${Math.round(delay)}ms...`);
            await sleep(delay);
        }
    }

    // All attempts failed
    return {
        success: false,
        error: lastError?.message || 'Unknown error',
        attempts: maxAttempts,
        totalTime: Date.now() - startTime
    };
}

/**
 * Specialized retry for fetch requests with proper error handling
 */
export async function retryFetch(
    url: string,
    init: RequestInit = {},
    options: RetryOptions = {}
): Promise<RetryResult<Response>> {
    return withRetry(async () => {
        const response = await fetch(url, init);
        
        // Convert HTTP errors to throwable errors for retry logic
        if (!response.ok) {
            const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
            (error as any).status = response.status;
            (error as any).response = response;
            throw error;
        }
        
        return response;
    }, options);
}

/**
 * Specialized retry for JSON API calls
 */
export async function retryJsonFetch<T>(
    url: string,
    init: RequestInit = {},
    options: RetryOptions = {}
): Promise<RetryResult<T>> {
    const fetchResult = await retryFetch(url, init, options);
    
    if (!fetchResult.success || !fetchResult.data) {
        return {
            success: false,
            error: fetchResult.error,
            attempts: fetchResult.attempts,
            totalTime: fetchResult.totalTime
        };
    }

    try {
        const jsonData = await fetchResult.data.json();
        return {
            success: true,
            data: jsonData,
            attempts: fetchResult.attempts,
            totalTime: fetchResult.totalTime
        };
    } catch (error: any) {
        return {
            success: false,
            error: `JSON parsing failed: ${error.message}`,
            attempts: fetchResult.attempts,
            totalTime: fetchResult.totalTime
        };
    }
}
